// Pure canvas rendering engine — no React, no DOM globals
// All data comes in via QRSettings; never reads refs or queries the DOM.
import { isFinder, isOn } from './qr-utils';
import { drawDot, drawEyeShape, drawPupil } from './qr-shapes';
function buildFill(ctx, w, h, c1, c2, fillType, angle) {
    if (fillType === 'solid')
        return c1;
    const rad = (angle * Math.PI) / 180;
    let g;
    if (fillType === 'linear') {
        const dx = Math.cos(rad) * w * 0.6;
        const dy = Math.sin(rad) * h * 0.6;
        g = ctx.createLinearGradient(w / 2 - dx, h / 2 - dy, w / 2 + dx, h / 2 + dy);
    }
    else if (fillType === 'radial') {
        g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w * 0.7);
    }
    else {
        // conic
        g = ctx.createConicGradient(rad, w / 2, h / 2);
    }
    g.addColorStop(0, c1);
    g.addColorStop(1, c2);
    return g;
}
function drawFrame(ctx, canvasW, canvasH, qrTop, qrH, s) {
    const { frame, frameColor, frameTextColor, frameLabel, frameLabelSize } = s;
    if (frame === 'none')
        return;
    const stripH = canvasH - qrH - (qrTop > 0 ? qrTop : 0);
    const labelFont = `bold ${Math.round(canvasW * frameLabelSize)}px sans-serif`;
    ctx.save();
    switch (frame) {
        case 'simple': {
            ctx.strokeStyle = frameColor;
            ctx.lineWidth = canvasW * 0.015;
            ctx.strokeRect(ctx.lineWidth / 2, ctx.lineWidth / 2, canvasW - ctx.lineWidth, canvasH - ctx.lineWidth);
            break;
        }
        case 'rounded': {
            const lw = canvasW * 0.015;
            ctx.strokeStyle = frameColor;
            ctx.lineWidth = lw;
            ctx.beginPath();
            ctx.roundRect(lw / 2, lw / 2, canvasW - lw, canvasH - lw, canvasW * 0.06);
            ctx.stroke();
            break;
        }
        case 'label-bottom': {
            const bandY = qrTop + qrH;
            ctx.fillStyle = frameColor;
            ctx.fillRect(0, bandY, canvasW, stripH);
            ctx.fillStyle = frameTextColor;
            ctx.font = labelFont;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(frameLabel, canvasW / 2, bandY + stripH / 2);
            break;
        }
        case 'label-top': {
            ctx.fillStyle = frameColor;
            ctx.fillRect(0, 0, canvasW, stripH);
            ctx.fillStyle = frameTextColor;
            ctx.font = labelFont;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(frameLabel, canvasW / 2, stripH / 2);
            break;
        }
        case 'badge': {
            const bandY = qrTop + qrH;
            ctx.fillStyle = frameColor;
            ctx.beginPath();
            ctx.roundRect(0, 0, canvasW, canvasH, canvasW * 0.06);
            ctx.fill();
            const pad = canvasW * 0.04;
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.roundRect(pad, qrTop - pad, canvasW - pad * 2, qrH + pad * 2, canvasW * 0.04);
            ctx.fill();
            ctx.fillStyle = frameTextColor;
            ctx.font = labelFont;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(frameLabel, canvasW / 2, bandY + stripH / 2);
            break;
        }
        case 'ticket': {
            const bandY = qrTop + qrH;
            ctx.strokeStyle = frameColor;
            ctx.setLineDash([canvasW * 0.03, canvasW * 0.02]);
            ctx.lineWidth = canvasW * 0.012;
            ctx.strokeRect(ctx.lineWidth / 2, qrTop - ctx.lineWidth / 2, canvasW - ctx.lineWidth, qrH + ctx.lineWidth);
            ctx.setLineDash([]);
            ctx.fillStyle = frameColor;
            ctx.fillRect(0, bandY, canvasW, stripH);
            ctx.fillStyle = frameTextColor;
            ctx.font = labelFont;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(frameLabel, canvasW / 2, bandY + stripH / 2);
            break;
        }
        case 'corners': {
            const a = canvasW * 0.12;
            const lw = canvasW * 0.025;
            ctx.strokeStyle = frameColor;
            ctx.lineWidth = lw;
            ctx.lineCap = 'square';
            const corners = [
                [lw / 2, lw / 2, 1, 1],
                [canvasW - lw / 2, lw / 2, -1, 1],
                [lw / 2, canvasH - lw / 2, 1, -1],
                [canvasW - lw / 2, canvasH - lw / 2, -1, -1],
            ];
            for (const [x, y, dx, dy] of corners) {
                ctx.beginPath();
                ctx.moveTo(x + dx * a, y);
                ctx.lineTo(x, y);
                ctx.lineTo(x, y + dy * a);
                ctx.stroke();
            }
            break;
        }
        case 'speech': {
            const tailH = canvasH * 0.1;
            ctx.strokeStyle = frameColor;
            ctx.lineWidth = canvasW * 0.015;
            ctx.beginPath();
            ctx.roundRect(0, 0, canvasW, canvasH - tailH, canvasW * 0.06);
            ctx.stroke();
            ctx.fillStyle = frameColor;
            ctx.beginPath();
            ctx.moveTo(canvasW * 0.38, canvasH - tailH);
            ctx.lineTo(canvasW * 0.5, canvasH);
            ctx.lineTo(canvasW * 0.62, canvasH - tailH);
            ctx.closePath();
            ctx.fill();
            break;
        }
        case 'neon': {
            const lw = canvasW * 0.015;
            ctx.shadowColor = frameColor;
            ctx.shadowBlur = canvasW * 0.04;
            ctx.strokeStyle = frameColor;
            ctx.lineWidth = lw;
            ctx.beginPath();
            ctx.roundRect(lw / 2, lw / 2, canvasW - lw, canvasH - lw, canvasW * 0.06);
            ctx.stroke();
            ctx.shadowBlur = 0;
            break;
        }
        case 'double': {
            const lw = canvasW * 0.012;
            ctx.strokeStyle = frameColor;
            ctx.lineWidth = lw;
            ctx.strokeRect(lw / 2, lw / 2, canvasW - lw, canvasH - lw);
            ctx.lineWidth = lw * 0.7;
            const gap = canvasW * 0.03;
            ctx.strokeRect(gap, gap, canvasW - gap * 2, canvasH - gap * 2);
            break;
        }
        default:
            break;
    }
    ctx.restore();
}
/**
 * Pixel-perfect QR renderer (scan-safe):
 * - integer module size
 * - integer padding
 * - quiet zone >= 4 modules guaranteed
 * - image smoothing disabled
 */
export function renderQR(m, cv, size, s) {
    const STRIP_FRAMES = ['label-bottom', 'ticket', 'badge'];
    const hasBottom = STRIP_FRAMES.includes(s.frame);
    const hasTop = s.frame === 'label-top';
    const stripH = hasBottom || hasTop ? Math.round(size * 0.2) : 0;
    cv.width = size;
    cv.height = size + (hasBottom ? stripH : 0) + (hasTop ? stripH : 0);
    const ctx = cv.getContext('2d');
    if (!ctx)
        return;
    // ✅ Critical: avoid anti-alias blur on module edges
    ctx.imageSmoothingEnabled = false;
    const n = m.length;
    const qrOriginY = hasTop ? stripH : 0;
    // Background (must be solid for iPhone)
    ctx.fillStyle = s.bg;
    ctx.fillRect(0, 0, cv.width, cv.height);
    // =========================================================
    // ✅ Pixel-perfect integer grid + guaranteed quiet zone
    // =========================================================
    const QUIET_MODULES = 4;
    // Largest integer cell that fits with quiet zone
    const maxCell = Math.floor(size / (n + QUIET_MODULES * 2));
    if (maxCell <= 0)
        return;
    // Try to honor user padding, but never allow fractional cells
    const desiredPad = Math.max(0, Math.floor(s.padding));
    const desiredCell = Math.floor((size - desiredPad * 2) / n);
    const cell = Math.max(1, Math.min(maxCell, desiredCell || maxCell));
    const qrSizePx = cell * n;
    // Center with integer padding. Because cell <= maxCell, this pad is >= 4*cell.
    const pad = Math.floor((size - qrSizePx) / 2);
    const qrLeft = pad;
    const qrTop = qrOriginY + pad;
    const fill = buildFill(ctx, size, size, s.c1, s.c2, s.fillType, s.gradAngle);
    // Finder patterns (eyes)
    const eyes = [
        { r: 0, c: 0 },
        { r: 0, c: n - 7 },
        { r: n - 7, c: 0 },
    ];
    for (const ep of eyes) {
        const ox = qrLeft + ep.c * cell;
        const oy = qrTop + ep.r * cell;
        ctx.fillStyle = s.eyeOut;
        ctx.save();
        drawEyeShape(ctx, ox, oy, cell, s.eyeStyle);
        ctx.fill('evenodd');
        ctx.restore();
        ctx.fillStyle = s.eyeIn;
        drawPupil(ctx, ox, oy, cell, s.pupilStyle, s.pupilScale, s.pupilRot);
    }
    // ✅ Scan-safe module sizing: avoid shrinking square modules
    const safeDotSize = s.dotStyle === 'square' || s.dotStyle === 'squarernd' || s.dotStyle === 'block'
        ? Math.max(0.98, s.dotSize)
        : s.dotSize;
    // Data dots
    if (s.dotStyle === 'liquid') {
        const mf = 1 + s.merge;
        ctx.fillStyle = fill;
        for (let r = 0; r < n; r++) {
            for (let c = 0; c < n; c++) {
                if (!m[r][c] || isFinder(r, c, n))
                    continue;
                const rad = cell * safeDotSize * 0.5 * mf;
                ctx.beginPath();
                ctx.arc(qrLeft + c * cell + cell * 0.5, qrTop + r * cell + cell * 0.5, rad, 0, Math.PI * 2);
                ctx.fill();
            }
        }
        for (let r = 0; r < n; r++) {
            for (let c = 0; c < n; c++) {
                if (!m[r][c] || isFinder(r, c, n))
                    continue;
                const cx = qrLeft + c * cell + cell * 0.5;
                const cy = qrTop + r * cell + cell * 0.5;
                const rad = cell * safeDotSize * 0.5 * mf;
                if (isOn(r, c + 1, m) && !isFinder(r, c + 1, n)) {
                    ctx.fillRect(cx, cy - rad, qrLeft + (c + 1) * cell + cell * 0.5 - cx, rad * 2);
                }
                if (isOn(r + 1, c, m) && !isFinder(r + 1, c, n)) {
                    ctx.fillRect(cx - rad, cy, rad * 2, qrTop + (r + 1) * cell + cell * 0.5 - cy);
                }
                if (isOn(r + 1, c, m) && isOn(r, c + 1, m) && isOn(r + 1, c + 1, m) && !isFinder(r + 1, c + 1, n)) {
                    ctx.fillRect(cx, cy, qrLeft + (c + 1) * cell + cell * 0.5 - cx, qrTop + (r + 1) * cell + cell * 0.5 - cy);
                }
            }
        }
    }
    else {
        ctx.fillStyle = fill;
        for (let r = 0; r < n; r++) {
            for (let c = 0; c < n; c++) {
                if (!m[r][c] || isFinder(r, c, n))
                    continue;
                // ✅ Integer size + integer placement (no sub-pixel edges)
                const dsz = Math.max(1, Math.round(cell * safeDotSize));
                const x = qrLeft + c * cell + Math.floor((cell - dsz) / 2);
                const y = qrTop + r * cell + Math.floor((cell - dsz) / 2);
                drawDot(ctx, x, y, dsz, s.dotStyle, s.roundness);
            }
        }
    }
    // Logo overlay (still disabled in your defaults; keep it that way for scanning)
    if (s.logoEnabled && s.logoImg) {
        const ls = size * s.logoSize;
        const lx = (size - ls) / 2;
        const ly = qrOriginY + (size - ls) / 2;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.roundRect(lx - ls * 0.12, ly - ls * 0.12, ls * 1.24, ls * 1.24, ls * 0.18);
        ctx.fill();
        ctx.drawImage(s.logoImg, lx, ly, ls, ls);
    }
    drawFrame(ctx, cv.width, cv.height, qrOriginY, size, s);
}
